import React, { useEffect, useRef } from 'react';
import { XIcon } from 'lucide-react';
import { useAnalyticsConsent } from '../../hooks/useAnalyticsConsent';
import { ConsentChoices } from './ConsentChoices';
interface CookieChoicesDialogProps {
  open: boolean;
  onClose: () => void;
}
const FOCUSABLE = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** Accessible modal opened from the footer. Focus is trapped and returned on close. */
export function CookieChoicesDialog({
  open,
  onClose
}: CookieChoicesDialogProps) {
  const snapshot = useAnalyticsConsent();
  const dialogRef = useRef<HTMLDivElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (!open) return undefined;
    returnFocusRef.current = document.activeElement as HTMLElement | null;
    dialogRef.current?.querySelector<HTMLElement>('h2')?.focus();
    return () => {
      returnFocusRef.current?.focus();
    };
  }, [open]);

  // Escape closes reliably while open, even if focus has fallen out of the dialog.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // When a choice swaps the buttons, the focused one unmounts. Keep focus inside the dialog.
  useEffect(() => {
    if (!open || !dialogRef.current) return;
    if (!dialogRef.current.contains(document.activeElement)) {
      dialogRef.current.querySelector<HTMLElement>('h2')?.focus();
    }
  }, [open, snapshot.choice, snapshot.message, snapshot.signalDenied, snapshot.storageAvailable]);
  if (!open) return null;
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Tab' || !dialogRef.current) return;
    const items = Array.from(dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE));
    if (items.length === 0) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && (document.activeElement === first || document.activeElement === dialogRef.current.querySelector('h2'))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };
  const status = snapshot.signalDenied ? 'Current choice: off, because of your browser’s privacy signal.' : snapshot.choice === 'granted' ? 'Current choice: analytics allowed.' : snapshot.choice === 'denied' ? 'Current choice: analytics off.' : 'Current choice: none yet. Analytics stay off unless you allow them.';
  return <div className="fixed inset-0 z-50 flex items-end justify-center p-3 sm:items-center sm:p-6" data-id="element-112" data-mp="3tckps3w92q8z">
      <div className="absolute inset-0 bg-ink/50" aria-hidden="true" onClick={onClose} data-id="element-113" data-mp="14mdswu3tx6q" />
      <div ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby="cookie-choices-title" onKeyDown={handleKeyDown} className="relative max-h-[90vh] w-full max-w-[460px] overflow-y-auto rounded-2xl border-2 border-ink bg-cream p-6 shadow-lift" data-id="element-114" data-mp="661wii8t7rly">
        <div className="mb-3 flex items-start justify-between gap-4" data-id="element-115" data-mp="1hp8y5basdab9">
          <h2 id="cookie-choices-title" tabIndex={-1} className="font-display text-[24px] leading-tight text-ink outline-none" data-id="element-116" data-mp="1cpd0dtsomf45">
            Cookie choices
          </h2>
          <button type="button" onClick={onClose} aria-label="Close cookie choices" className="-mr-2 -mt-2 inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-ink transition-colors duration-150 ease-out hover:bg-cream-field" data-id="element-117" data-mp="25c7co3fci86j">
            <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-118" data-mp="2t2jtk3skjf99" />
          </button>
        </div>
        <p className="mb-4 text-[15px] font-bold text-ink" aria-live="polite" data-id="element-119" data-mp="2obbfuvk7h7qa">
          {status}
        </p>
        <ConsentChoices snapshot={snapshot} allowWithdraw onLinkClick={onClose} data-id="element-120" data-mp="27v0x2zxhturg" />
      </div>
    </div>;
}